import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronDown } from "lucide-react";
import { BaseSheet } from "@/components/common/BaseSheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDebouncedValue } from "@/hooks/ui/useDebouncedValue";
import { useUsersWithSubscriptions } from "@/hooks/users/useUsersWithSubscriptions";
import { AgentService, type CloneOptions } from "@/services/agent/core";
import { Agent } from "@/types/agent/agent";
import { ProfileWithSubscription } from "@/types/auth/auth";

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_RESULTS_LIMIT = 20;

const FIELD_LABEL = "mb-2 block text-sm font-semibold text-gray-700";
const PRIMARY_CTA =
  "bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg";

export type AssignableUser = Pick<ProfileWithSubscription, "id" | "full_name" | "email">;

/** Admin copies are test copies first: they start on the CS test login, never on the admin's own account. */
const CS_TEST_LOGIN: AssignableUser = {
  id: "0c4215e4-7419-46cf-bada-7f9a9f387a76",
  full_name: "CS test login",
  email: "cs-test@helpgenie-test.example.com",
};

interface CloneAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  agent: Agent | null;
  onConfirm: (name: string, user: AssignableUser, options: CloneOptions) => void;
  isLoading?: boolean;
}

interface CloneChoiceProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
}

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? "" : "s"}`;

const UserLabel = ({ user }: { user: AssignableUser }) =>
  user.full_name ? (
    <span className="min-w-0 truncate">
      {user.full_name} <span className="text-gray-500">· {user.email}</span>
    </span>
  ) : (
    <span className="min-w-0 truncate">{user.email}</span>
  );

const CloneChoice = ({ id, label, description, checked, onCheckedChange, disabled }: CloneChoiceProps) => (
  <div className="flex items-start gap-3">
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={(value) => onCheckedChange(value === true)}
      disabled={disabled}
      className="mt-0.5"
    />
    <label htmlFor={id} className="min-w-0 cursor-pointer">
      <span className="block text-sm font-medium text-gray-900">{label}</span>
      <span className="block break-words text-sm text-gray-600">{description}</span>
    </label>
  </div>
);

export function CloneAgentModal({
  isOpen,
  onClose,
  agent,
  onConfirm,
  isLoading,
}: CloneAgentModalProps) {
  const [cloneName, setCloneName] = useState("");
  const [selectedUser, setSelectedUser] = useState<AssignableUser | null>(null);
  const [isUserPickerOpen, setIsUserPickerOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const debouncedUserSearch = useDebouncedValue(userSearch.trim(), SEARCH_DEBOUNCE_MS);
  const { data: usersData, isFetching: isSearchingUsers } = useUsersWithSubscriptions(
    { search: debouncedUserSearch },
    1,
    SEARCH_RESULTS_LIMIT,
    "full_name",
    "asc",
  );
  const users = usersData?.profiles ?? [];
  const isSearchPending = isSearchingUsers || userSearch.trim() !== debouncedUserSearch;

  const [copyKnowledgeBase, setCopyKnowledgeBase] = useState(true);
  const [copyResources, setCopyResources] = useState(true);
  const [provisionMailbox, setProvisionMailbox] = useState(false);
  const sourceAgentId = agent?.id;
  const { data: sourceAgent, isLoading: isCheckingSource } = useQuery({
    queryKey: ["clone-source-genie", sourceAgentId],
    queryFn: () => AgentService.getAgentDetails(sourceAgentId!),
    enabled: isOpen && !!sourceAgentId,
  });
  const knowledgeDocumentCount: number = sourceAgent?.knowledge_base?.length ?? 0;
  const resourceLinkCount: number = sourceAgent?.info_links?.links?.length ?? 0;
  const resourceFileCount: number = sourceAgent?.info_links?.documents?.length ?? 0;
  const hasResources = resourceLinkCount + resourceFileCount > 0 || !!sourceAgent?.info_links?.banner_image_url;
  const sourceMailbox = sourceAgent?.mailbox?.status === "provisioned" ? sourceAgent.mailbox.email_address : null;
  const sourcePhoneNumbers = [
    ...new Set(
      [
        sourceAgent?.phone,
        ...(sourceAgent?.transfer_phones ?? []),
        ...(sourceAgent?.escalation_phones ?? []),
      ]
        .map((phone) => phone?.number)
        .filter((number): number is string => !!number),
    ),
  ];

  useEffect(() => {
    if (isOpen && agent) {
      setCopyKnowledgeBase(true);
      setCopyResources(true);
      setProvisionMailbox(false);
      setCloneName(`${agent.name} (Copy)`);
      setSelectedUser(CS_TEST_LOGIN);
    }
  }, [agent, isOpen]);

  const handleUserPickerOpenChange = (open: boolean) => {
    setIsUserPickerOpen(open);
    if (!open) setUserSearch("");
  };

  const handleUserSelect = (user: AssignableUser) => {
    setSelectedUser(user);
    handleUserPickerOpenChange(false);
  };

  const canConfirm = !!cloneName.trim() && !!selectedUser && !isLoading && !isCheckingSource;

  const handleConfirm = () => {
    if (!canConfirm || !selectedUser) return;
    onConfirm(cloneName, selectedUser, {
      copyKnowledgeBase,
      copyResources: hasResources && copyResources,
      provisionMailbox: !!sourceMailbox && provisionMailbox,
    });
    handleClose();
  };

  const handleClose = () => {
    setCloneName("");
    setSelectedUser(null);
    handleUserPickerOpenChange(false);
    onClose();
  };

  return (
    <BaseSheet
      isOpen={isOpen}
      onClose={handleClose}
      title="Clone Genie"
      description={`Create a copy of "${agent?.name ?? ""}" with a new name and assign it to a user.`}
      size="lg"
      footer={
        <div className="flex w-full justify-start gap-3">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm} className={PRIMARY_CTA}>
            {isLoading ? "Cloning..." : "Clone"}
          </Button>
        </div>
      }
    >
      <div className="space-y-6 px-6 py-6 sm:px-8">
        <div className="space-y-4">
          <div>
            <label htmlFor="clone-genie-name" className={FIELD_LABEL}>
              Genie Name
            </label>
            <Input
              id="clone-genie-name"
              placeholder="Enter genie name"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              disabled={isLoading}
              autoFocus
              className="w-full"
            />
          </div>
          <div>
            <span className={FIELD_LABEL}>Assign to User</span>
            <Popover modal open={isUserPickerOpen} onOpenChange={handleUserPickerOpenChange}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={isLoading}
                  aria-label="Assign to user"
                  className="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-genie-primary disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {selectedUser ? (
                    <UserLabel user={selectedUser} />
                  ) : (
                    <span className="text-muted-foreground">Select a user</span>
                  )}
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                sideOffset={6}
                className="z-[120] w-[var(--radix-popover-trigger-width)] p-0"
              >
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search by name or email..."
                    value={userSearch}
                    onValueChange={setUserSearch}
                    className="text-sm"
                  />
                  <CommandList className="max-h-[min(18rem,var(--radix-popover-content-available-height))]">
                    <CommandEmpty>{isSearchPending ? "Searching..." : "No users found."}</CommandEmpty>
                    {users.length > 0 && (
                      <CommandGroup>
                        {users.map((user) => (
                          <CommandItem
                            key={user.id}
                            value={user.id}
                            onSelect={() => handleUserSelect(user)}
                            className="flex cursor-pointer items-center gap-2"
                          >
                            <UserLabel user={user} />
                            {selectedUser?.id === user.id && (
                              <Check className="ml-auto h-4 w-4 shrink-0 text-genie-primary" />
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {isCheckingSource && <p className="text-sm text-gray-600">Checking what this genie has attached...</p>}

        {(knowledgeDocumentCount > 0 || hasResources) && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-gray-700">What to copy</p>
              <p className="text-sm text-gray-600">The copy gets its own files, separate from the original's.</p>
            </div>
            {knowledgeDocumentCount > 0 && (
              <CloneChoice
                id="clone-copy-knowledge"
                label="Knowledge base"
                description={`${plural(knowledgeDocumentCount, "document")} will be copied to the new owner's library and attached to the copy.`}
                checked={copyKnowledgeBase}
                onCheckedChange={setCopyKnowledgeBase}
                disabled={isLoading}
              />
            )}
            {hasResources && (
              <CloneChoice
                id="clone-copy-resources"
                label="Resources"
                description={`${plural(resourceLinkCount, "link")} and ${plural(resourceFileCount, "file")} will be copied.`}
                checked={copyResources}
                onCheckedChange={setCopyResources}
                disabled={isLoading}
              />
            )}
          </div>
        )}

        {(sourceMailbox || sourcePhoneNumbers.length > 0) && (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-semibold text-gray-700">Connected services</p>
              <p className="text-sm text-gray-600">The original keeps its own; none of them move to the copy.</p>
            </div>
            {sourceMailbox && (
              <CloneChoice
                id="clone-provision-mailbox"
                label="Set up a new mailbox for the copy"
                description={`The original uses ${sourceMailbox}. The copy gets its own address.`}
                checked={provisionMailbox}
                onCheckedChange={setProvisionMailbox}
                disabled={isLoading}
              />
            )}
            {sourcePhoneNumbers.length > 0 && (
              <div>
                <p className="text-sm font-medium text-gray-900">Phone numbers are not copied</p>
                <p className="break-words text-sm text-gray-600">
                  The original uses {sourcePhoneNumbers.join(", ")}. The copy starts with no phone numbers; add them
                  on the copy afterwards if it needs any.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </BaseSheet>
  );
}
